import { Box, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { KTag } from '@/ui-library/components';
import { TransferCandidate } from '@/assets/icons/ats';
import { ApplicationData } from '@/components/candidate-job-slider-v1/types/application.interface';
import { selectUsers } from '@/store/users/selectors';
import { useSelector } from 'react-redux';

export interface ReportsTransferredBadgeProps {
  type: 'from' | 'to';
  jobName: string;
  transferredBy?: { name?: string };
}

export const ReportsTransferredBadge = ({
  type,
  jobName,
  transferredBy,
}: ReportsTransferredBadgeProps) => {
  const byLabel = transferredBy?.name ? ` by ${transferredBy.name}` : '';
  const tooltipLabel =
    type === 'from'
      ? `Transferred from ${jobName || 'this job'}${byLabel}`
      : `Transferred to ${jobName || 'another job'}${byLabel}`;

  return (
    <Tooltip label={tooltipLabel} hasArrow whiteSpace="pre-line">
      <Box
        as="span"
        cursor="pointer"
        flexShrink={0}
        display="flex"
        mr="5px"
        mt="1px"
      >
        <KTag
          value={
            <Flex alignItems="center" gap="6px">
              <Icon as={TransferCandidate} w="16px" h="16px" />
            </Flex>
          }
          p="2px 8px"
          borderRadius="8px"
          bg="pastel-6-light"
          color="pastel-6-dark"
        />
      </Box>
    </Tooltip>
  );
};

export interface TransferredBadgeProps {
  transferInfo: ApplicationData['transfer_info'];
  showTooltipToLabel?: boolean;
  isReferredForm?: boolean;
  isIconOnly?: boolean;
}

interface TransferStep {
  fromJob: string;
  toJob: string;
  by: string;
}

export const TransferredBadge = ({
  transferInfo,
  showTooltipToLabel = false,
  isReferredForm = false,
}: TransferredBadgeProps) => {
  const users = useSelector(selectUsers);
  if (!transferInfo) {
    return null;
  }
  const { history = [], applications = {} } = transferInfo;
  // @ts-expect-error - transferred_from is the referral form structure, not in the base transfer_info type
  const transferredFrom = transferInfo?.transferred_from as
    | {
        job: { id: number; name: string };
        referral_id: number;
        transferred_at: string;
        transferred_by_id: number;
      }
    | undefined;

  const transferredBy = users.find(
    (user) => user.id === transferredFrom?.transferred_by_id
  );

  // Build transfer chain from history
  const transferSteps: TransferStep[] = [];

  history.forEach((appId) => {
    const app = applications[appId];
    if (app?.transferred_out) {
      transferSteps.push({
        fromJob: app.application?.job?.name || '',
        toJob:
          applications[app.transferred_out.to_application_id]?.application?.job
            ?.name || '',
        by: app.transferred_out.by?.name || '',
      });
    }
  });

  // Generate tooltip label showing transfer chain
  const tooltipLabel = isReferredForm
    ? `Candidate transferred from the ${transferredFrom?.job?.name} job by ${transferredBy?.name}`
    : transferSteps.length > 0
      ? transferSteps
          .map(
            (step) =>
              `Candidate transferred from the ${step.fromJob} job by ${step.by}`
          )
          .join('\n')
      : 'Candidate transferred';

  const tooltipTolabel =
    transferSteps.length > 0
      ? transferSteps
          .map(
            (step) =>
              `Candidate transferred to the ${step.toJob} job by ${step.by}`
          )
          .join('\n')
      : 'Candidate transferred';

  return (
    <Tooltip
      label={showTooltipToLabel ? tooltipTolabel : tooltipLabel}
      hasArrow
      whiteSpace="pre-line"
    >
      <Box
        as="span"
        cursor="pointer"
        flexShrink={0}
        display="flex"
        mr="5px"
        mt="1px"
      >
        <KTag
          value={
            <Flex alignItems="center" gap="6px">
              <Icon as={TransferCandidate} w="16px" h="16px" />
            </Flex>
          }
          p={'2px !important'}
          borderRadius={'4px !important'}
          bg="pastel-6-light"
          color="pastel-6-dark"
        />
      </Box>
    </Tooltip>
  );
};
